import { Navigate, Outlet } from 'react-router-dom';
import { AppRoutes, navigateToRoute } from '../navigation/routes';
import { useHasGlobalPermissions, useHasStudyClaims } from '../helpers/auth/auth-helper';
import { IDoctorProfileDto, Permissions, StudyRoleClaimValue } from 'src/services/api/api-client';
import { useScopedTranslation } from '../application/localisation/useScopedTranslation';
import { InformationPageContainer } from 'src/components/informationPageContainer/InformationPage.Container';
import noStudyImage from 'src/assets/img/patient/empty_table.svg';
import { FC } from 'react';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { StudyNotFoundError } from 'src/helpers/customErrors/customErrors';
import { Dashboard as DashboardPage } from 'src/containers/dashboard/dashboard.container';

export const FirstPageResolver: FC<{ profile: IDoctorProfileDto }> = ({ profile }) => {
  const { t } = useScopedTranslation('ProfileProvider');
  const { hasOneOfPermissions } = useHasGlobalPermissions();

  if (profile.studies.length > 1) return <Navigate to={AppRoutes.Studies} />;

  if (profile.studies.length === 1) return <Navigate to={navigateToRoute.dashboard(profile.studies[0])} />;

  if (hasOneOfPermissions(Permissions.StudyManage, Permissions.UserManage, Permissions.RoleAssign))
    return <Navigate to={AppRoutes.Admin} />;

  if (!profile.studies.length)
    return <InformationPageContainer header={t('NoStudyTitle')} text={t('NoStudyText')} img={noStudyImage} />;

  return <Outlet />;
};

export const StudyPageResolver: FC = () => {
  const study = useStudy();
  const { hasStudyClaim } = useHasStudyClaims();

  if (!study) throw new StudyNotFoundError();

  if (hasStudyClaim(StudyRoleClaimValue.PatientView)) return <DashboardPage />;

  if (hasStudyClaim(StudyRoleClaimValue.StudyConfigure))
    return <Navigate to={navigateToRoute.studySettings(study.id)} />;

  if (hasStudyClaim(StudyRoleClaimValue.ParticipantsView))
    return <Navigate to={navigateToRoute.participants(study.id)} />;

  return <Navigate to={AppRoutes.NoPermission} />;
};
